"use client"

import {
  Button,
  cn,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  Spinner,
  Tab,
  Tabs,
} from "@heroui/react"
import { useInfiniteQuery } from "@tanstack/react-query"
import { useTranslations } from "next-intl"
import { useEffect, useMemo, useRef, useState } from "react"
import type { Key } from "react"
import { AssetListType, type ContentSource } from "@/apis/asset"
import { assetListQueryOption } from "@/apis/query/asset"
import { RelationRobotQueryOption } from "@/apis/query/room"
import { uploadFile } from "@/apis/common"
import { useToast } from "@/provider/ToastProvider"

/** 上传图片的最小宽高（px） */
const MIN_IMAGE_SIZE = 400

/** 读取图片文件的原始宽高 */
function getImageSize(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve({ width: img.naturalWidth, height: img.naturalHeight })
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error("Failed to load image"))
    }
    img.src = url
  })
}

const ImagePlusIcon = ({ className }: { className?: string }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M14.167 2.5H5.833A3.333 3.333 0 0 0 2.5 5.833v8.334A3.333 3.333 0 0 0 5.833 17.5h8.334a3.333 3.333 0 0 0 3.333-3.333V11.25M15 2.5v5M17.5 5h-5M3.333 14.167 7.5 10c.46-.443 1.207-.443 1.667 0l2.5 2.5M10.833 11.667 12.5 10c.46-.443 1.207-.443 1.667 0L17.5 13.333"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const AlertCircleIcon = ({ className }: { className?: string }) => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
    <path
      d="M12 8v4M12 16h.01"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const CheckIcon = ({ className }: { className?: string }) => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M2.5 6.2 4.8 8.5 9.5 3.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

/** 选择多张图片文件 */
function pickFiles(onPicked: (files: File[]) => void) {
  const input = document.createElement("input")
  input.type = "file"
  input.accept = "image/*"
  input.multiple = true
  input.onchange = () => {
    const files = Array.from(input.files ?? [])
    if (files.length) onPicked(files)
  }
  input.click()
}

/** 单张图片卡片：可选中、hover 高亮，可带底部名称条（角色卡片） */
function ImageCard({
  src,
  label,
  selected,
  disabled,
  onToggle,
}: {
  src: string
  /** 底部渐变上显示的名称（角色卡片用） */
  label?: string
  selected: boolean
  disabled?: boolean
  onToggle: () => void
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled && !selected}
      className={cn(
        "group/card relative aspect-[180/248] overflow-hidden rounded-xl border-2 transition-colors",
        selected ? "border-purple-blue" : "border-transparent",
        disabled && !selected && "cursor-not-allowed opacity-50",
      )}
    >
      <img src={src} alt="" className="size-full object-cover" />
      {/* 底部名称条：深色渐变衬托 */}
      {label && (
        <span className="absolute inset-x-0 bottom-0 flex h-8 items-end rounded-b-[10px] bg-gradient-to-b from-transparent to-theme-black/40 px-2 pb-1.5">
          <span className="truncate text-xs font-medium capitalize text-white">
            {label}
          </span>
        </span>
      )}
      {/* 选中圆点 */}
      <span
        className={cn(
          "absolute left-2 top-2 grid size-5 place-content-center rounded-full border transition-colors",
          selected
            ? "border-purple-blue bg-purple-blue text-theme-white"
            : "border-theme-white/80 bg-theme-black/20 opacity-0 group-hover/card:opacity-100",
        )}
      >
        {selected && <CheckIcon className="size-3" />}
      </span>
    </button>
  )
}

/** 选中的图片：url + 来源（素材库 / 角色 / 上传）+ 来源内容 id（素材=product_id，角色=robot_id） */
export type PickedImage = {
  url: string
  source: ContentSource
  contentId: number
}

type Props = {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  /** 最多可选数量 */
  max?: number
  /** 打开时预勾选的素材（content_source 为 assets），用 id 关联回显 */
  initialSelected?: { url: string; id: number }[]
  /** 已被其他槽位占用的素材 id：置灰禁选，避免重复选择同一素材 */
  excludeIds?: number[]
  /** 打开时预选中的角色（content_source 为 robot），用于回显 */
  initialRobot?: { id: number; url: string }
  onConfirm: (images: PickedImage[]) => void
}

export function SelectImageModal({
  isOpen,
  onOpenChange,
  max = 9,
  initialSelected,
  excludeIds,
  initialRobot,
  onConfirm,
}: Props) {
  const t = useTranslations('generatePage')
  const { showToast } = useToast()
  const [tab, setTab] = useState<"character" | "upload" | "asset">("character")
  // 素材库选中项：以 id 关联（content_source 为 assets），回显时用 id 判断是否选中
  const [selected, setSelected] = useState<{ url: string; id: number }[]>([])
  // 角色只能选一个：背景图优先，无背景用头像
  const [selectedRobot, setSelectedRobot] = useState<{
    id: number
    name: string
    image: string
  } | null>(null)
  const [uploading, setUploading] = useState(false)

  // 素材库：只看生成成功的图片资产
  const assetQuery = useInfiniteQuery({
    ...assetListQueryOption(AssetListType.Image, 1),
    enabled: isOpen && tab === "asset",
  })

  // 角色：用户关联的机器人
  const robotQuery = useInfiniteQuery({
    ...RelationRobotQueryOption,
    enabled: isOpen && tab === "character",
  })

  const excluded = useMemo(() => new Set(excludeIds ?? []), [excludeIds])

  const assets = useMemo<{ url: string; id: number }[]>(
    () =>
      (assetQuery.data?.pages ?? []).flatMap((page: any) =>
        (page?.list ?? [])
          .map((item: any) => ({
            url: item?.image_url,
            id: Number(item?.product_id) || 0,
          }))
          .filter((a: { url?: string }) => a.url),
      ),
    [assetQuery.data],
  )

  const robots = useMemo<{ id: number; name: string; image: string }[]>(
    () =>
      (robotQuery.data?.pages ?? []).flatMap((page: any) =>
        (Array.isArray(page) ? page : [])
          .map((item: any) => ({
            id: Number(item?.robots_id) || 0,
            name: item?.robot_name ?? "",
            // 背景图优先，没有背景用头像
            image: item?.background_image || item?.avatar,
          }))
          .filter((r: { image?: string }) => r.image),
      ),
    [robotQuery.data],
  )

  // 滚动到底自动加载下一页（素材 / 角色列表共用容器）
  const activeQuery = tab === "character" ? robotQuery : assetQuery
  const scrollRef = useRef<HTMLDivElement>(null)
  const handleScroll = () => {
    const el = scrollRef.current
    if (!el || !activeQuery.hasNextPage || activeQuery.isFetchingNextPage) return
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 200) {
      activeQuery.fetchNextPage()
    }
  }

  // 每次打开重置状态：预选角色/素材回显并切到对应 Tab
  useEffect(() => {
    if (isOpen) {
      const init = initialSelected ?? []
      setSelected(init)
      // 角色回显：名称在列表加载后由卡片自行展示，这里只需 id 与图片
      setSelectedRobot(
        initialRobot ? { id: initialRobot.id, name: "", image: initialRobot.url } : null,
      )
      setTab(initialRobot ? "character" : init.length ? "asset" : "character")
      setUploading(false)
    }
  }, [isOpen, initialSelected, initialRobot])

  // 已选总数：角色占用一个参考图名额，与素材统一计数
  const total = selected.length + (selectedRobot ? 1 : 0)

  const toggle = (asset: { url: string; id: number }) => {
    if (selected.some((s) => s.id === asset.id)) {
      setSelected((prev) => prev.filter((s) => s.id !== asset.id))
      return
    }
    // 单选：跨 Tab 直接替换，选素材时清掉已选角色
    if (max === 1) {
      setSelectedRobot(null)
      setSelected([asset])
      return
    }
    if (total >= max) return
    setSelected((prev) => [...prev, asset])
  }

  // 角色单选：再点取消，点其他直接替换；名额已满且未选角色时不可再选
  const toggleRobot = (robot: { id: number; name: string; image: string }) => {
    if (selectedRobot?.id === robot.id) {
      setSelectedRobot(null)
      return
    }
    // 单选：跨 Tab 直接替换，选角色时清掉已选素材
    if (max === 1) {
      setSelected([])
      setSelectedRobot(robot)
      return
    }
    if (!selectedRobot && total >= max) return
    setSelectedRobot(robot)
  }

  // 上传即确定：上传到 OSS 拿到线上地址，注册为素材、回传并关闭弹窗
  const uploadAndConfirm = async (files: File[]) => {
    const images = files.filter((f) => f.type.startsWith("image/")).slice(0, max)
    if (!images.length) return
    setUploading(true)
    try {
      // 尺寸校验：低于 400x400 的图片直接拦截并提示
      const sizes = await Promise.all(
        images.map((f) => getImageSize(f).catch(() => null)),
      )
      const hasTooSmall = sizes.some(
        (s) => !s || s.width < MIN_IMAGE_SIZE || s.height < MIN_IMAGE_SIZE,
      )
      if (hasTooSmall) {
        showToast({ type: "error", message: t("imageTooSmall") })
        return
      }
      const results = await Promise.all(images.map((f) => uploadFile(f)))
      const picked: PickedImage[] = results
        .filter((r) => r?.status === 200 && r?.data)
        .map((r) => ({ url: r.data as string, source: "upload", contentId: 0 }))
      if (!picked.length) return
      onConfirm(picked)
      onOpenChange(false)
    } finally {
      setUploading(false)
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    const files = Array.from(e.clipboardData.files).filter((f) =>
      f.type.startsWith("image/"),
    )
    if (files.length) uploadAndConfirm(files)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    const files = Array.from(e.dataTransfer.files).filter((f) =>
      f.type.startsWith("image/"),
    )
    if (files.length) uploadAndConfirm(files)
  }

  // 角色与素材一并回传：角色单选（content_id 为 robot_id），并占一个参考图名额
  const confirm = () => {
    const picked: PickedImage[] = [
      ...(selectedRobot
        ? [{ url: selectedRobot.image, source: "robot" as const, contentId: selectedRobot.id }]
        : []),
      ...selected.map((s) => ({
        url: s.url,
        source: "assets" as const,
        contentId: s.id,
      })),
    ]
    if (!picked.length) return
    onConfirm(picked)
    onOpenChange(false)
  }
  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      size="5xl"
      scrollBehavior="inside"
      classNames={{
        base: "max-w-[1100px] w-full rounded-xl bg-surface",
        body: "p-0",
      }}
    >
      <ModalContent>
        <ModalBody>
          <div className="flex flex-col px-4 lg:px-6 pt-5">
            <h2 className="text-base font-bold text-theme-primary">{t('selectImage')}</h2>

            <div className="mt-4 flex items-end justify-between border-b border-theme-primary/10">
              <Tabs
                aria-label="Select image source"
                selectedKey={tab}
                onSelectionChange={(key: Key) =>
                  setTab(key as "character" | "upload" | "asset")
                }
                variant="underlined"
                classNames={{
                  base: "w-full",
                  tabList: "gap-8 p-0",
                  tab: "px-0 h-9 max-w-fit",
                  tabContent:
                    "text-sm font-medium text-theme-primary/60 group-data-[selected=true]:text-theme-primary",
                  cursor: "bg-purple-blue",
                }}
              >
                <Tab key="character" title={t('character')} />
                <Tab key="upload" title={t('uploadImage')} />
                <Tab key="asset" title={t('asset')} />
              </Tabs>
              {tab !== "upload" && (
                <p className="shrink-0 whitespace-nowrap pb-2 text-xs font-medium">
                  {/* 角色与素材统一计数：角色也占参考图名额 */}
                  <span className="text-purple-blue">{`${total}/${max}`}</span>{" "}
                  <span className="text-t-subtitle">{t('selected')}</span>
                </p>
              )}
            </div>
          </div>

          <div className="px-4 lg:px-6 py-4">
            {tab === "character" ? (
              robotQuery.isLoading ? (
                <div className="grid h-[420px] place-content-center">
                  <Spinner color="current" />
                </div>
              ) : robots.length === 0 ? (
                <div className="grid h-[420px] place-content-center text-sm text-t-subtitle">
                  {t('noData')}
                </div>
              ) : (
                <div
                  ref={scrollRef}
                  onScroll={handleScroll}
                  className="max-h-[420px] overflow-y-auto"
                >
                  <div className="grid grid-cols-[repeat(auto-fill,minmax(140px,1fr))] gap-4">
                    {robots.map((robot, i) => (
                      <ImageCard
                        key={robot.id || i}
                        src={robot.image}
                        label={robot.name}
                        selected={selectedRobot?.id === robot.id}
                        disabled={max > 1 && !selectedRobot && total >= max}
                        onToggle={() => toggleRobot(robot)}
                      />
                    ))}
                  </div>
                  {robotQuery.isFetchingNextPage && (
                    <div className="grid place-content-center py-3">
                      <Spinner size="sm" color="current" />
                    </div>
                  )}
                </div>
              )
            ) : tab === "upload" ? (
              <div
                onPaste={handlePaste}
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                className="grid h-[420px] place-content-center justify-items-center gap-3 rounded-lg border-[0.5px] border-dashed border-theme-primary/20 bg-create-card"
              >
                {uploading ? (
                  <>
                    <Spinner color="current" />
                    <p className="text-sm text-t-subtitle">{t('uploading')}</p>
                  </>
                ) : (
                  <>
                    <Button
                      radius="full"
                      onPress={() => pickFiles(uploadAndConfirm)}
                      className="h-11 gap-1.5 bg-purple-blue px-6 text-sm font-medium text-theme-white"
                    >
                      <ImagePlusIcon className="size-5" />
                      {t('uploadImage')}
                    </Button>
                    <p className="text-sm text-t-subtitle">
                      {t('pasteTip')}
                    </p>
                    <p className="text-xs text-t-subtitle">
                      {t('minSizeTip')}
                    </p>
                  </>
                )}
              </div>
            ) : assetQuery.isLoading ? (
              <div className="grid h-[420px] place-content-center">
                <Spinner color="current" />
              </div>
            ) : assets.length === 0 ? (
              <div className="grid h-[420px] place-content-center text-sm text-t-subtitle">
                {t('noAssets')}
              </div>
            ) : (
              <div
                ref={scrollRef}
                onScroll={handleScroll}
                className="max-h-[420px] overflow-y-auto"
              >
                <div className="grid grid-cols-[repeat(auto-fill,minmax(140px,1fr))] gap-4">
                  {assets.map((asset, i) => (
                    <ImageCard
                      key={asset.id || i}
                      src={asset.url}
                      selected={selected.some((s) => s.id === asset.id)}
                      disabled={
                        excluded.has(asset.id) ||
                        (max > 1 && total >= max)
                      }
                      onToggle={() => toggle(asset)}
                    />
                  ))}
                </div>
                {assetQuery.isFetchingNextPage && (
                  <div className="grid place-content-center py-3">
                    <Spinner size="sm" color="current" />
                  </div>
                )}
              </div>
            )}
          </div>
        </ModalBody>
        {tab !== "upload" && (
          <ModalFooter className="items-center border-t border-theme-primary/5">
            {/* 选择角色可获得更多曝光 */}
            {tab === "character" && (
              <span className="mr-auto flex h-6 items-center gap-1 rounded-full bg-purple-blue/20 px-2.5 text-xs text-purple-blue/70">
                <AlertCircleIcon className="size-3 shrink-0" />
                {t('chooseCharacterTip')}
              </span>
            )}
            <Button
              radius="md"
              isDisabled={total === 0}
              onPress={confirm}
              className="bg-purple-blue px-6 text-sm font-medium text-theme-white"
            >
              {t('confirm')}
            </Button>
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  )
}
